import React from "react";

import { Column } from "commons/types/table";

import { TableRowWrapperPartialParamsType } from "commons/ui/datatable/TableRows";

type TableRowPropsType<T> = TableRowWrapperPartialParamsType<T> & {
    readonly row: T;
    readonly index: number;
};

function RenderRow<T>({ row, column }: { row: T; column: Column<T> }) {
    const rowRendered: any = React.useMemo(() => column.render(row), [column, row]);

    if (column.emptyContainer) {
        return "";
    }

    if (typeof rowRendered === "string" || rowRendered === null) {
        return rowRendered ? rowRendered : "-";
    }

    return rowRendered;
}

function TableRow<T>({
    row,
    columns,
    hideTableHead,
    onRowClick,
    disableOnRowClick,
    emphasizeRow,
    index,
    dragAndDropHook,
}: TableRowPropsType<T>) {
    let isClickable = true;

    if (disableOnRowClick !== undefined) {
        if (disableOnRowClick(row)) {
            isClickable = false;
        }
    }

    if (onRowClick === undefined) {
        isClickable = false;
    }

    let extraTdClass = "";

    if (emphasizeRow !== undefined) {
        extraTdClass = emphasizeRow(row);
    }

    const trProps: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLTableRowElement>,
        HTMLTableRowElement
    > = {
        className: isClickable ? "clickable" : "",
    };

    if (dragAndDropHook !== undefined) {
        trProps.draggable = true;

        trProps.className += " " + dragAndDropHook.classes[index]?.join(" ");

        trProps.onDragStart = () => dragAndDropHook.onDragStart(index);
        trProps.onDragEnd = () => dragAndDropHook.onDragEnd(index);
        trProps.onDragOver = (event) => dragAndDropHook.onDragOver(event);
        trProps.onDragEnter = () => dragAndDropHook.onDragEnter(index);
        trProps.onDragLeave = () => dragAndDropHook.onDragLeave(index);
        trProps.onDrop = () => dragAndDropHook.onDrop(index);
    }

    return (
        <tr {...trProps}>
            {columns.map((column) => (
                <td
                    data-testid="column"
                    key={column.id}
                    style={{
                        textAlign: column.align ? column.align : "left",
                        padding: column.padding,
                        color: column.renderColor ? column.renderColor(row) : undefined,
                        width: hideTableHead && column.width ? column.width : undefined,
                    }}
                    className={`${column.linkContainer ? " link-container " : ""} ${extraTdClass} ${
                        column.emphasizeCell ? column.emphasizeCell(row) : ""
                    }`}
                    colSpan={column?.colSpan ?? 1}
                    onClick={() => {
                        if (onRowClick && column.linkContainer === undefined) {
                            onRowClick(row);
                        }
                    }}
                >
                    <RenderRow row={row} column={column} />
                </td>
            ))}
        </tr>
    );
}

export default TableRow;
